/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:38:23                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:24:31                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * DrawerForm - drawer form container component
    * formChange - handle form change
    * submitForm: trigger form submit event
  * EditCreateDrawer: manage edit/create form
    * openEditDrawer - Open drawer for edit form
    * openCreateDrawer - Open drawer for create form
    * closeDrawer - close drawer
    * submitEvent - trigger onSubmit callback
*/

/* Imports */
import { t } from "i18next";
import { Button, IconButton } from "@material-tailwind/react";
import {
  PlusCircleIcon,
  PencilIcon
} from "@heroicons/react/24/outline";
import { ComponentType, useState, ReactNode } from "react";
import toast from "react-hot-toast";
/***/

/* Components */
import { useDrawerLayoutDispatch } from "../drawer-layout/DrawerLayout";
import { FieldNames, validator } from "../../services/form-validator/form-validator";
import { CustomerValidator } from "../data/customer/customer-validator";
/***/

/* Styles */
import "./EditCreateDrawer.scss";
/***/

/**
* DrawerForm - drawer form container component
* @param data - form data
* @param FormComponent - form sub component
* @param onSubmit - submit callback
* @param closeDrawer - close drawer function
*/
function DrawerForm<T>({
  data,
  FormComponent,
  onSubmit,
  closeDrawer,
  isEditing,
  validator: formValidator,
  fieldNames,
  entityName
}: {
  data: T,
  onSubmit: (data: T) => void | Promise<void>,
  closeDrawer: () => void,
  isEditing?: boolean,
  validator?: any,
  fieldNames?: FieldNames,
  entityName?: string
  FormComponent: ComponentType<{
    data?: T,
    onChange: (name: string, value: any) => void,
    onSubmit: () => void,
    submitted?: boolean,
    isEditing?: boolean
  }>
}) {
  const getInitialData = (): T => {
    if (data) {
      if (entityName === "Language") {
        return {
          ...data,
          label: (data as any).label || "",
          isoCode: (data as any).isoCode || "",
          default: (data as any).default !== undefined ? (data as any).default : false,
          enabled: (data as any).enabled !== undefined ? (data as any).enabled : true
        } as T;
      }
      return data;
    }

    // Pour les customers, initialiser les champs obligatoires
    if (entityName === "customer") {
      return {
        name: "",
        activitySector: null,
        languages: [],
        nip: "",
        badPayer: false,
        purchaseOrderMandatory: false,
        reportTemplate: null,
        // Opt-in, matching the DB column (send_intervention_report_email NOT NULL DEFAULT false).
        // Defaulting to true here silently signed every newly created customer up for automatic
        // report emails, contradicting what the backend records for a customer created elsewhere.
        sendInterventionReportEmail: false,
        contactInfo: {
          email: "",
          phone: "",
          fax: "",
          notes: ""
        },
        address: null
      } as T;
    }

    if (entityName === "contact") {
      return {
        firstName: "",
        lastName: "",
        function: "",
        civility: "",
        contactInfo: {
          email: "",
          phone: "",
          fax: "",
          notes: ""
        }
      } as T;
    }

    if (entityName === "Language") {
      return {
        label: "",
        isoCode: "",
        default: false,
        enabled: true
      } as T;
    }

    return {} as T;
  };

  const [dataState, setDataState] = useState<T>(getInitialData());
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
  * formChange - handle form change
  * @param name - object key name
  * @param value - object key value
  */
  const formChange = (name: string, value: any) => {
    setDataState((prev) => {
      const tmp = { ...prev as any };
      tmp[name] = value;
      return tmp;
    });
  };
  /***/

  /**
  * submitForm: trigger form submit event
  */
  const submitForm = async () => {
    setSubmitted(true);

    // Validation spécifique selon le type d'entité
    if (entityName) {
      let validationErrors: string[] = [];

      // Utiliser le validateur spécialisé pour les customers
      if (entityName === "customer") {
        validationErrors = CustomerValidator.validate(dataState, t);
      } else {
        // Pour les autres entités, utiliser le validateur générique
        validationErrors = validator.validateEntityData(entityName, dataState, t);
      }

      if (validationErrors.length > 0) {
        const errorMessage = `${t("common:formError")}: ${validationErrors.join(", ")}`;
        toast.error(errorMessage);
        return;
      }
    }

    // Validation du formulaire si un validateur est fourni
    if (formValidator && fieldNames) {
      if (!validator.isFormValid(dataState, formValidator)) {
        const errorMessage = validator.getFormErrorMessage(dataState, formValidator, fieldNames, t);
        toast.error(errorMessage);
        return;
      }
    }

    setIsLoading(true);
    try {
      await onSubmit(dataState);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  /***/

  return (
    <div className="drawer-form">
      <div className="content">
        {isEditing ?
          <FormComponent data={dataState}
            onChange={formChange}
            onSubmit={() => onSubmit(dataState)}
            submitted={submitted} isEditing />
          :
          <FormComponent data={dataState}
            onChange={formChange}
            onSubmit={() => onSubmit(dataState)}
            submitted={submitted} />
        }

      </div>


      <div className="footer">
        <div className="text-xs text-gray-400 italic flex-1">{t("common:mandatoryField")}</div>

        <Button
          variant="filled"
          color="white"
          className="btn-cancel normal-case font-thin border whitespace-nowrap"
          onClick={closeDrawer}
        >
          {t("common:cancel")}
        </Button>

        <Button
          variant="filled"
          className="bg-blue-500 normal-case font-thin whitespace-nowrap"
          onClick={() => submitForm()}
          disabled={isLoading}
        >
          {t("common:save", "Save")}
        </Button>
      </div>
    </div>
  );
}
/***/

/**
* EditCreateDrawer: manage edit/create form
* create or edit entity via drawer form
* @param data - form data, editing purpose
* @param entityName - entity name for header display
* @param ForComponent - form sub component
* @param onSubmit - on submit event
* @param isNotCreatable - disable creation button
* @param isNotEditable - disable edition button
*/
export default function EditCreateDrawer<T>({
  data,
  entityName = "",
  FormComponent,
  onSubmit,
  isNotCreatable,
  isNotEditable,
  createButtonText,
  editButtonText,
  drawerTitle,
  disabled,
  validator: formValidator,
  fieldNames,
  iconColor = "blue",
  customEditTrigger
}: {
  data?: T,
  entityName?: string,
  FormComponent: ComponentType<{
    data?: T,
    onChange: (name: string, value: any) => void,
    onSubmit: () => void,
    submitted?: boolean,
    isEditing?: boolean
  }>,
  onSubmit?: (value: T) => Promise<void>,
  isNotEditable?: boolean,
  isNotCreatable?: boolean,
  createButtonText?: string,
  editButtonText?: string,
  drawerTitle?: string,
  disabled?: boolean,
  validator?: any,
  fieldNames?: FieldNames,
  iconColor?: any,
  customEditTrigger?: (open: () => void) => ReactNode
}) {
  const dispatch = useDrawerLayoutDispatch();

  /**
  * openEditDrawer - Open drawer to customer edit 
  */
  const openEditDrawer = () => {
    if (disabled) return;
    dispatch({
      type: "open",
      title: drawerTitle || (t("common:edit", "Edit") + " " + t(`common:entities:${entityName}`, entityName)),
      content: <DrawerForm data={data as T}
        onSubmit={submitEvent}
        closeDrawer={closeDrawer}
        FormComponent={FormComponent}
        isEditing={isNotCreatable && !isNotEditable}
        validator={formValidator}
        fieldNames={fieldNames}
        entityName={entityName} />
    });
  };
  /***/

  /**
  * openCreateDrawer - Open drawer to customer create 
  */
  const openCreateDrawer = () => {
    if (disabled) return;
    dispatch({
      type: "open",
      title: drawerTitle || (t("common:create", "Create") + " " + t(`common:entities:${entityName}`, entityName)),
      content: <DrawerForm data={data as T}
        onSubmit={submitEvent}
        closeDrawer={closeDrawer}
        FormComponent={FormComponent}
        validator={formValidator}
        fieldNames={fieldNames}
        entityName={entityName} />
    });
  };
  /***/

  /**
  * closeDrawer - close drawer 
  */
  const closeDrawer = () => {
    dispatch({ type: "close" });
  };
  /***/

  /**
  * submitEvent - trigger onSubmit callback
  * @param data - form data
  */
  const submitEvent = (data: T) => {
    if (onSubmit) {
      return onSubmit(data)
        .then(() => closeDrawer())
        .catch((err) => {
          console.error(err);
          throw err;
        });
    }
    return Promise.resolve();
  };
  /***/

  if (isNotEditable && isNotCreatable) return null;

  return (
    <div className="edit-create-drawer">
      {!isNotEditable && (
        <>
          {customEditTrigger ? (
            customEditTrigger(openEditDrawer)
          ) : !editButtonText ? (
            <IconButton onClick={openEditDrawer}
              variant="text"
              color={iconColor}
              className="icon-btn"
              disabled={disabled || !data}
              size="sm">
              <PencilIcon className="w-[20px] h-[20px]" />
            </IconButton>
          ) : (
            <Button
              variant="filled"
              color="blue"
              className="normal-case font-thin"
              onClick={openEditDrawer}
              disabled={disabled}
            >
              {editButtonText}
            </Button>
          )}
        </>
      )}

      {!isNotCreatable && (
        <>
          {!createButtonText ?
            <IconButton onClick={openCreateDrawer}
              variant="text"
              color={iconColor}
              className="icon-btn"
              size="sm"
              disabled={disabled}>

              <PlusCircleIcon className="w-[24px]" />
            </IconButton>
            :
            <Button
              variant="filled"
              color="blue"
              className="normal-case font-thin"
              onClick={openCreateDrawer}
              disabled={disabled}
            >
              {createButtonText}
            </Button>
          }
        </>
      )}

    </div>
  );
}
/***/
